import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, inject, provideAppInitializer, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule } from '@angular/forms';
import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import localeEs from '@angular/common/locales/es';
registerLocaleData(localeEsAr, 'es-AR');
registerLocaleData(localeEs, 'es');
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { LoginComponent } from './auth/login/login.component';
import { LoginDialogComponent } from './auth/loginDialog/loginDialog.component';
import { AuthInterceptor } from './_interceptor/auth-interceptor';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ConfigService } from '@servicios/config.service';
import { ErrorDialogComponentComponent } from './_featuresModules/error-dialog-component/error-dialog-component.component';
import { BackupComponent } from './backup/backup.component';
import { LoadingComponent } from './loading/loading.component';
import { GlobalErrorHandler } from './_helpers/globalErrorHandler';
import { graphqlModule } from './graphql.module';
import { DateAdapter, MatNativeDateModule, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { SharedModules } from '@SharedModules/shared-modules.module';
import { ErrorInterceptor } from './_interceptor/error-interceptor';
import { ShowChartButtonComponent } from '@agGridCellRender/show-chart-button/show-chart-button.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SignedIconComponent } from '@agGridCellRender/signed-icon/signed-icon.component';
import { NgxEditorModule } from 'ngx-editor'
import { RegisterComponent } from './auth/register/register.component';
import { PrivacyModule } from './privacy/privacy.module';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DialogModule } from 'primeng/dialog';
import { HelpDialogComponent } from './help/help-panel/help-panel.component';
import { DropdownModule } from 'primeng/dropdown';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { PasswordModule } from 'primeng/password';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { modalConfig } from './_pipes/modalConfig';
import { nameModalComponent } from './_modelos/enum/enums';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { FormAdminConfigComponent } from './_featuresModules/adminConfigModule/form-admin-config/form-admin-config.component';
import { FormTechConfig } from './_featuresModules/techConfigModule/generals/form-tech-config/form-tech-config.component';
import { FormPatientComponent } from './_featuresModules/comercial/patient/form-patient/form-patient.component';
import { FormCustomerSupplierComponent } from './_featuresModules/comercial/customerSupplier/form-customer-supplier/form-customer-supplier.component';
import { FormAssayComponent } from './_featuresModules/techConfigModule/assay/form-assay/form-assay.component';
import { FormAssayGroupComponent } from './_featuresModules/techConfigModule/assayGroup/form-assay-group/form-assay-group.component';
import { InventoryModule } from './_featuresModules/inventory/inventory.module';
import { EquipmentsModule } from './_featuresModules/equipments/equipments.module';

import { CalendarModule, DateAdapter as CalendarDateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

export function loadConfiguration(configService: ConfigService) {
  return () => configService.loadConfig();
}


const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        }
    }
});



@NgModule({ declarations: [
        AppComponent,

        LoginComponent,
        RegisterComponent,
        LoginDialogComponent,
        ShowChartButtonComponent,
        SignedIconComponent,
        ErrorDialogComponentComponent,
        BackupComponent,
        LoadingComponent,
        HelpDialogComponent
    ],
    bootstrap: [AppComponent],
    imports: [BrowserModule,
        AppRoutingModule,
        FormsModule,
        FlexLayoutModule,
        graphqlModule,
        SharedModules,
        MatNativeDateModule,
        DragDropModule,
        NgxEditorModule,
        PrivacyModule,
        DynamicDialogModule,
        OverlayPanelModule,
        DialogModule,
        DropdownModule,
        SelectModule,
        FloatLabelModule,
        PasswordModule,
        InputTextModule,
        ButtonModule,
        ConfirmDialogModule,
        CalendarModule.forRoot({
          provide: CalendarDateAdapter,
          useFactory: adapterFactory,
        }),
        InventoryModule,
        EquipmentsModule
    ],
    providers: [
        DialogService,
        MessageService,
        ConfirmationService,
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        ConfigService,
        provideAppInitializer(() => {
        const initializerFn = (loadConfiguration)(inject(ConfigService));
        return initializerFn();
      }),
        { provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: LOCALE_ID, useValue: 'es-AR' },
        provideAnimationsAsync(),
        providePrimeNG({
            ripple: false,
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: false
                }
            },
            translation: {
                accept: 'Aceptar',
                reject: 'Cancelar',
                dayNames: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
                dayNamesShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
                dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
                monthNames: ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"],
                monthNamesShort: ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"],
                today: 'Hoy',
                clear: 'Limpiar',
                dateFormat: 'dd/mm/yy',
                weekHeader: 'Sm',
                chooseYear: 'Elegir Año',
                chooseMonth: 'Elegir Mes',
                chooseDate: 'Elegir Fecha',
                prevDecade: 'Década anterior',
                nextDecade: 'Década siguiente',
                prevYear: 'Año anterior',
                nextYear: 'Año siguiente',
                prevMonth: 'Mes anterior',
                nextMonth: 'Mes siguiente',
                prevHour: 'Hora anterior',
                nextHour: 'Hora siguiente',
                prevMinute: 'Minuto anterior',
                nextMinute: 'Minuto siguiente',
                prevSecond: 'Segundo anterior',
                nextSecond: 'Segundo siguiente',
                am: 'AM',
                pm: 'PM'
            }
        }),
        provideHttpClient(withInterceptorsFromDi())
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
    constructor(private registry: modalConfig) {
        this.registry.register(nameModalComponent.FormAdminConfigComponent, FormAdminConfigComponent);
        this.registry.register(nameModalComponent.FormTechConfig, FormTechConfig);
        this.registry.register(nameModalComponent.FormPatientComponent, FormPatientComponent);
        this.registry.register(nameModalComponent.FormCustomerSupplierComponent, FormCustomerSupplierComponent);
        this.registry.register(nameModalComponent.FormAssayComponent, FormAssayComponent);
        this.registry.register(nameModalComponent.FormAssayGroupComponent, FormAssayGroupComponent);
    }
}

